"use client";

import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

const CandidateSurface = styled(Box)(() => ({
  width: "100%",
  maxWidth: "1040px",
  margin: "0 auto",
}));

const SkeletonRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(280px, 1.6fr) minmax(200px, 1fr) 88px",
  gap: theme.spacing(3),
  alignItems: "center",
  padding: theme.spacing(1.6, 0.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gridTemplateRows: "auto auto",
    columnGap: theme.spacing(1.25),
    rowGap: theme.spacing(0.25),
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gridTemplateRows: "auto auto",
    columnGap: theme.spacing(1.25),
    rowGap: theme.spacing(0.25),
    margin: theme.spacing(0, 0, 1),
    padding: theme.spacing(1.35, 1.25),
  },
}));

export default function CandidateListSkeleton({
  complete = false,
  query = "",
}) {
  return (
    <CandidateSurface
      aria-busy="true"
      sx={{ mt: { xs: 7.5, md: 17.5 }, px: { xs: 2, md: 0 } }}
    >
      {query && (
        <Typography
          variant="h5"
          sx={{ fontWeight: 560, mb: 0.35, overflowWrap: "anywhere" }}
        >
          People matching “{query}”
        </Typography>
      )}
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", fontSize: "0.9rem" }}
      >
        Select a person, or refine your search with a company or city.
      </Typography>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          gap: 1.25,
          height: { xs: 100, md: 80 },
          px: { xs: 0, md: 0.5 },
        }}
      >
        <CircularProgress size={18} thickness={4} color="inherit" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {complete ? "Opening results" : "Finding matching people"}
        </Typography>
      </Box>

      <Box aria-hidden="true">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonRow key={index}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minWidth: 0,
                gridColumn: "1",
                gridRow: { xs: "1", lg: "auto" },
              }}
            >
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton width="55%" height={22} />
                <Skeleton width="72%" height={18} sx={{ mt: 0.35 }} />
              </Box>
            </Box>
            <Box
              sx={{
                pl: { xs: "50px", lg: 0 },
                gridColumn: { xs: "1", lg: "2" },
                gridRow: { xs: "2", lg: "auto" },
              }}
            >
              <Skeleton width="58%" height={21} />
              <Skeleton
                width="40%"
                height={18}
                sx={{ mt: 0.35, display: { xs: "none", sm: "block" } }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.5,
                gridColumn: { xs: "2", lg: "3" },
                gridRow: { xs: "1", lg: "auto" },
              }}
            >
              <Skeleton variant="circular" width={30} height={30} />
              <Skeleton
                variant="circular"
                width={30}
                height={30}
                sx={{ display: { xs: "none", sm: "block" } }}
              />
            </Box>
          </SkeletonRow>
        ))}
      </Box>
    </CandidateSurface>
  );
}
